import React, { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';
import { AuthProvider } from './contexts/AuthContext';
import { CartProvider } from './contexts/CartContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { SUPPORTED_LANGS, getCookie } from './i18n';
import Header from './components/Header';
import Footer from './components/Footer';
import LoadingScreen from './components/LoadingScreen';
import LocaleDetector from './components/LocaleDetector';
import AnnouncementBar from './components/AnnouncementBar';

// Lazy-loaded pages for code splitting
const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Account = lazy(() => import('./pages/Account'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const Checkout = lazy(() => import('./pages/Checkout'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const SubcategoryPage = lazy(() => import('./pages/SubcategoryPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const ShopByShade = lazy(() => import('./pages/ShopByShade'));
const OurStory = lazy(() => import('./pages/OurStory'));
const Notifications = lazy(() => import('./pages/Notifications'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const PolicyPage = lazy(() => import('./pages/PolicyPage'));
const HairCareGuide = lazy(() => import('./pages/HairCareGuide'));
const TrackOrder = lazy(() => import('./pages/TrackOrder'));
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const Chatbot = lazy(() => import('./components/Chatbot'));
const BackToTop = lazy(() => import('./components/BackToTop'));

const CursorGlow = () => {
  const glowRef = useRef(null);
  const { theme } = useTheme();

  useEffect(() => {
    if (theme !== 'dark') return;
    const handleMove = (e) => {
      if (glowRef.current) {
        glowRef.current.style.left = `${e.clientX}px`;
        glowRef.current.style.top = `${e.clientY}px`;
      }
    };
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, [theme]);

  if (theme !== 'dark') return null;
  return <div ref={glowRef} className="cursor-glow" />;
};

function LangSync() {
  const { lang } = useParams();
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && SUPPORTED_LANGS.includes(lang) && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return null;
}

function LangRedirect() {
  const savedLang = getCookie('zelina-lang');
  const browserLang = navigator.language?.substring(0, 2);
  const targetLang = SUPPORTED_LANGS.includes(savedLang) ? savedLang : SUPPORTED_LANGS.includes(browserLang) ? browserLang : 'en';
  return <Navigate to={`/${targetLang}`} replace />;
}

function LocalizedRoutes() {
  const { lang } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    if (lang && !SUPPORTED_LANGS.includes(lang)) {
      navigate('/en', { replace: true });
    }
  }, [lang, navigate]);

  return (
    <>
      <LangSync />
      <a href="#main-content" className="skip-to-content">Skip to content</a>
      <AnnouncementBar />
      <Header />
        <main
          id="main-content"
          className="flex-1 pt-[66px] animate-fadeIn"
        >
          <Suspense fallback={<div className="flex-1 flex items-center justify-center min-h-[50vh]"><div className="w-6 h-6 border-2 border-[#C9A227] border-t-transparent rounded-full animate-spin" /></div>}>
          <Routes>
            <Route index element={<Home />} />
            <Route path="products" element={<Navigate to="collections/straight" replace />} />
            <Route path="products/:variantSlug" element={<ProductDetailPage />} />
            <Route path="collections/:category/:subcategory" element={<SubcategoryPage />} />
            <Route path="collections/:category" element={<CategoryPage />} />
            <Route path=":category/:subcategory" element={<SubcategoryPage />} />
            <Route path=":category" element={<CategoryPage />} />
            <Route path="shop-by-shade" element={<ShopByShade />} />
            <Route path="track-order" element={<TrackOrder />} />
            <Route path="about" element={<OurStory />} />
            <Route path="cart" element={<Cart />} />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password" element={<ResetPassword />} />
            <Route path="account" element={<Account />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="policy/:policySlug" element={<PolicyPage />} />
            <Route path="hair-care-guide" element={<HairCareGuide />} />
            <Route path="admin" element={<AdminDashboard />} />
          </Routes>
          </Suspense>
        </main>
      <Footer />
      <LocaleDetector />
    </>
  );
}

function AppContent() {
  const [loading, setLoading] = useState(() => {
    return !sessionStorage.getItem('zelina-loaded');
  });

  const handleLoadComplete = useCallback(() => {
    setLoading(false);
    sessionStorage.setItem('zelina-loaded', '1');
  }, []);

  return (
    <>
      {loading && <LoadingScreen onComplete={handleLoadComplete} />}
      <CursorGlow />
      <div className="noise-overlay" />
      <div
        className={`min-h-screen flex flex-col transition-opacity duration-700 ${loading ? 'opacity-0' : 'opacity-100'}`}
        style={{ background: 'var(--bg-base)', color: 'var(--text-primary)', position: 'relative', zIndex: 2 }}
      >
        <AppRoutes />
        <Suspense fallback={null}><Chatbot /></Suspense>
        <Suspense fallback={null}><BackToTop /></Suspense>
        <Toaster position="bottom-right" richColors />
      </div>
    </>
  );
}

function AppRoutes() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<div className="flex-1 flex items-center justify-center min-h-[50vh]"><div className="w-6 h-6 border-2 border-[#C9A227] border-t-transparent rounded-full animate-spin" /></div>}>
    <Routes>
      <Route path="/" element={<LangRedirect />} />
      <Route path="/admin/*" element={<AdminLayout />} />
      <Route path="/:lang/*" element={<LocalizedRoutes />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider>
      <CurrencyProvider>
      <AuthProvider>
        <CartProvider>
          <Router>
            <AppContent />
          </Router>
        </CartProvider>
      </AuthProvider>
      </CurrencyProvider>
    </ThemeProvider>
  );
}

export default App;
App;
